import React, { useState, useCallback } from 'react';
import { ClipboardCopy, Check } from 'lucide-react';

// Reusable component for displaying an ID with a copy-to-clipboard icon
const CopyableId = ({ label, value, onValueClick, valueClassName = '', buttonClassName = '' }) => {
  const [copied, setCopied] = useState(false);

  // Handles the copy action and stops the event from bubbling up
  const handleCopyClick = useCallback((e) => {
    e.stopPropagation(); // Prevent click from triggering parent handlers (like onValueClick)
    if (!value) return;
    if (!value) return; // Don't try to copy null/undefined
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500); // Hide indicator after 1.5 seconds
    }).catch(err => {
      console.error('Failed to copy text: ', err);
      // Optionally handle copy error feedback here
    });
  }, [value]);

  // Handles the click on the value itself, if a handler is provided
  const handleValueClick = useCallback((e) => {
    if (onValueClick) {
      onValueClick(e); // Call the provided handler
    }
  }, [onValueClick]);


  if (!value) return null; // Don't render if no value

  // Determine dynamic classes for the value span based on whether it's clickable
  const valueClasses = `font-medium mr-1 ${valueClassName} ${
    onValueClick ? 'text-blue-600 cursor-pointer hover:underline' : ''
  }`;

  // Determine dynamic classes for the button
  const buttonClasses = `p-0.5 rounded hover:bg-gray-200 focus:outline-none focus:ring-1 focus:ring-blue-500 transition-colors relative ${buttonClassName}`;


  return (
    <span className="inline-flex"> {/* Use inline-flex for alignment, removed items-center */}
      {label && <span className="mr-1">{label}:</span>}
      <span
        className={valueClasses}
        onClick={handleValueClick} // Attach the value click handler
        title={onValueClick ? "View details" : ""} // Add title if clickable
      >
        {value}
      </span>
      <button
        onClick={handleCopyClick} // Use the stopPropagation handler
        className={buttonClasses}
        title={`Copy ${label || 'ID'}`}
        aria-label={`Copy ${label || 'ID'}`}
      >
        {copied ? (
          <Check className="h-3 w-3 text-green-600" /> // Green check icon for success
        ) : (
          <ClipboardCopy className="h-3 w-3 text-gray-500" /> // Copy icon
        )}
      </button>
    </span>
  );
};

export default CopyableId;
